import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, ChevronRight, Users, CheckCircle, XCircle } from 'lucide-react';
import { analyticsAPI } from '../utils/api';
import DateRangeFilter from './DateRangeFilter';

const getSuccessLevel = (ratio) => {
  if (ratio === null || ratio === undefined) return null;
  if (ratio >= 90) return { label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' };
  if (ratio >= 75) return { label: 'Good', color: 'text-teal-600', bg: 'bg-teal-100', border: 'border-teal-200' };
  if (ratio >= 60) return { label: 'Average', color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200' };
  return { label: 'Needs Improvement', color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' };
};

export function SuccessRatioCard({ isOfficer = false, onOfficerClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showOfficers, setShowOfficers] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedDays, customStartDate, customEndDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getSuccessRatio(selectedDays, customStartDate, customEndDate);
      setData(res.data);
    } catch (err) {
      console.log('Could not fetch success ratio');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (days) => {
    setSelectedDays(days);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleCustomDateChange = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const level = data?.success_ratio !== null ? getSuccessLevel(data?.success_ratio) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="success-ratio-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Trophy className="text-amber-500" size={20} />
          {isOfficer ? 'My Success Ratio' : 'Company Success Ratio'}
        </h3>
        <DateRangeFilter
          selectedDays={selectedDays}
          onFilterChange={handleFilterChange}
          startDate={customStartDate}
          endDate={customEndDate}
          onCustomDateChange={handleCustomDateChange}
          compact={true}
        />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-slate-100 rounded-lg"></div>
        </div>
      ) : data ? (
        <>
          {/* Main Ratio Display */}
          <div className={`p-4 rounded-lg ${level?.bg || 'bg-slate-50'} ${level?.border || 'border-slate-200'} border mb-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-4xl font-bold ${level?.color || 'text-slate-400'}`}>
                  {data.success_ratio !== null ? `${data.success_ratio}%` : '--'}
                </p>
                {level && (
                  <p className={`text-sm font-medium ${level.color} mt-1`}>{level.label}</p>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">{data.approved}</p>
                    <p className="text-xs text-slate-500">Approved</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-red-600">{data.rejected}</p>
                    <p className="text-xs text-slate-500">Rejected</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">{data.total_completed} total completed</p>
              </div>
            </div>
          </div>

          {/* Visual Progress Bar */}
          {data.total_completed > 0 && (
            <div className="mb-4">
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-200">
                <div 
                  className="bg-green-500 transition-all duration-500"
                  style={{ width: `${(data.approved / data.total_completed) * 100}%` }}
                ></div>
                <div 
                  className="bg-red-500 transition-all duration-500"
                  style={{ width: `${(data.rejected / data.total_completed) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span className="flex items-center gap-1">
                  <CheckCircle size={12} className="text-green-500" /> Approved
                </span>
                <span className="flex items-center gap-1">
                  <XCircle size={12} className="text-red-500" /> Rejected
                </span>
              </div>
            </div>
          )}

          {/* No completed cases */}
          {data.total_completed === 0 && (
            <div className="text-center py-4">
              <Trophy size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No completed cases in this period</p>
            </div>
          )}

          {/* Officer Breakdown (for admins) */}
          {!isOfficer && data.officer_breakdown && data.officer_breakdown.length > 0 && (
            <div>
              <button
                onClick={() => setShowOfficers(!showOfficers)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all"
                data-testid="toggle-officer-success"
              >
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">By Case Officer</span>
                </div>
                <ChevronRight size={18} className={`text-slate-400 transition-transform ${showOfficers ? 'rotate-90' : ''}`} />
              </button>
              
              {showOfficers && (
                <div className="mt-2 space-y-2" data-testid="officer-success-list">
                  {data.officer_breakdown.map((officer) => {
                    const officerLevel = getSuccessLevel(officer.success_ratio);
                    return (
                      <div 
                        key={officer.officer_id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${officerLevel?.bg} ${officerLevel?.border} cursor-pointer hover:shadow-sm`}
                        onClick={() => onOfficerClick && onOfficerClick(officer.officer_id)}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{officer.officer_name}</p>
                          <p className="text-xs text-slate-500">
                            {officer.approved}/{officer.total_completed} approved
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${officerLevel?.color}`}>{officer.success_ratio}%</p>
                          <p className={`text-xs ${officerLevel?.color}`}>{officerLevel?.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-6">
          <Trophy size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">Could not load success ratio data</p>
        </div>
      )}
    </div>
  );
}
